import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";

import { AlertDialog } from "@/shared/components/AlertDialog";
import {
  ResourceView,
  useResourceView,
  type Filter,
} from "@/shared/components/ResourceView";
import type { UserGroupRecord } from "@/shared/domain/userGroups";
import {
  useAddUserGroupMembers,
  useCreateUserGroup,
  useDeleteUserGroup,
  useRemoveUserGroupMembers,
  useUpdateUserGroup,
  useUserGroups,
} from "@/shared/domain/userGroups/hooks";
import { DeleteIcon, EditIcon } from "@/shared/foundation/icons/mui";
import {
  DataTable,
  buildColumnsForPreferences,
  createColumnHelper,
  deriveLoadingState,
  type ColDef,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { RobotoConflictException } from "@/shared/http";

import {
  USER_GROUP_FILTER_DEFINITIONS,
  userGroupMatchesFilters,
} from "./filterUserGroups";
import { useMemberLabels } from "./useMemberLabels";
import {
  UserGroupMemberPicker,
  type MemberOption,
} from "./UserGroupMemberPicker";
import styles from "./UserGroupsPanel.module.css";

const columnHelper = createColumnHelper<UserGroupRecord>();

const DEFAULT_VISIBLE_COLUMN_IDS = ["name", "members"];

/** Groups with at most this many members list them in the Members column. */
const MEMBER_SUMMARY_THRESHOLD = 5;

const explanation =
  "User Groups define who a feature should be rolled out to.\n\n" +
  "The groups should be fairly narrowly-constrained, and they should become progressively larger so that we have a " +
  "'ramp' that features can progress down before they go out.";

export function UserGroupsPanel() {
  const userGroupsQuery = useUserGroups();
  const { data: groups = [], error } = userGroupsQuery;
  const createMutation = useCreateUserGroup();
  const updateMutation = useUpdateUserGroup();
  const addMembersMutation = useAddUserGroupMembers();
  const removeMembersMutation = useRemoveUserGroupMembers();
  const deleteMutation = useDeleteUserGroup();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingGroup, setEditingGroup] =
    React.useState<UserGroupRecord | null>(null);
  const [name, setName] = React.useState("");
  const [selectedMembers, setSelectedMembers] = React.useState<MemberOption[]>(
    [],
  );
  const [formError, setFormError] = React.useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] =
    React.useState<UserGroupRecord | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  function openCreate() {
    setEditingGroup(null);
    setName("");
    setSelectedMembers([]);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(group: UserGroupRecord) {
    setEditingGroup(group);
    setName(group.name);
    setSelectedMembers(group.members.map(memberOptionFromId));
    setFormError(null);
    setDialogOpen(true);
  }

  // The memoized columns render the Edit button; route through a ref so
  // they needn't rebuild whenever `openEdit` does.
  const openEditRef = React.useRef(openEdit);
  openEditRef.current = openEdit;

  function closeDialog() {
    setDialogOpen(false);
    setFormError(null);
  }

  async function handleSubmit() {
    if (!name.trim()) {
      setFormError("Name is required.");
      return;
    }
    const members = selectedMembers.map((m) => m.value);
    try {
      if (editingGroup) {
        // Editing a group splits into up to two server calls: an
        // optional rename via the bulk update endpoint when the name
        // changed, then reconcile membership via the granular
        // add/remove endpoints. The granular endpoints take only
        // deltas, so concurrent edits by another admin to members this
        // admin did not touch are preserved. Each step is skipped when
        // there is nothing to do, so a save with no changes issues zero
        // round-trips.
        if (name.trim() !== editingGroup.name) {
          await updateMutation.mutateAsync({
            userGroupId: editingGroup.user_group_id,
            updates: { name: name.trim() },
          });
        }

        // Compute the delta between the group's persisted members and
        // the admin's current dialog selection: ids newly selected are
        // added, ids dropped from the prior list are removed.
        const previousIds = new Set(editingGroup.members);
        const nextIds = new Set(members);
        const toAdd = members.filter((id) => !previousIds.has(id));
        const toRemove = editingGroup.members.filter((id) => !nextIds.has(id));

        if (toAdd.length > 0) {
          await addMembersMutation.mutateAsync({
            userGroupId: editingGroup.user_group_id,
            request: { members: toAdd },
          });
        }
        if (toRemove.length > 0) {
          await removeMembersMutation.mutateAsync({
            userGroupId: editingGroup.user_group_id,
            request: { members: toRemove },
          });
        }
      } else {
        await createMutation.mutateAsync({ name: name.trim(), members });
      }
      closeDialog();
    } catch (err) {
      // Any failed step (rename, add, remove, or create) leaves the
      // dialog open with the admin's input intact and surfaces the
      // server's message inline. On edit, prior steps that succeeded
      // are not rolled back; the admin re-submits to retry the rest.
      setFormError(err instanceof Error ? err.message : "An error occurred.");
    }
  }

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    addMembersMutation.isPending ||
    removeMembersMutation.isPending;
  const submitLabel = editingGroup ? "Save" : "Create";

  const view = useResourceView<UserGroupRecord>({
    getRowId: (row) => row.user_group_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });
  const { table } = view;

  // The groups endpoint returns every group in one response and has no
  // search, so filter and page in memory.
  const structuredFilters = React.useMemo<Filter[]>(
    () => (view.filters.type === "structured" ? view.filters.filters : []),
    [view.filters],
  );
  const matchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";
  const filteredGroups = React.useMemo(
    () =>
      groups.filter((g) =>
        userGroupMatchesFilters(g, structuredFilters, matchMode),
      ),
    [groups, structuredFilters, matchMode],
  );
  const { currentPage, pageSize } = table;
  const pageRows = React.useMemo(() => {
    const start = currentPage * pageSize;
    return filteredGroups.slice(start, start + pageSize);
  }, [filteredGroups, currentPage, pageSize]);
  const hasNextPage = (currentPage + 1) * pageSize < filteredGroups.length;

  // Resolve names only for the members on screen: those listed in the page's
  // Members cells and those of the group being edited.
  const labelledMemberIds = React.useMemo(
    () => [
      ...pageRows
        .filter((g) => g.members.length <= MEMBER_SUMMARY_THRESHOLD)
        .flatMap((g) => g.members),
      ...(editingGroup?.members ?? []),
    ],
    [pageRows, editingGroup],
  );
  const memberLabels = useMemberLabels(labelledMemberIds);

  // Chips for members loaded with the group carry only their id; relabel
  // them once their names resolve.
  const pickerValue = React.useMemo(
    () =>
      selectedMembers.map((m) => ({
        ...m,
        label: memberLabels.get(m.value) ?? m.label,
      })),
    [selectedMembers, memberLabels],
  );

  // Columns start hidden under DataTable's `defaultColDef.hide: true`;
  // `buildColumnsForPreferences` shows the default ids, and the actions
  // column carries its own `hide: false`. There is no column picker here, so
  // columns aren't movable: one dragged out could not be brought back.
  const columns = React.useMemo<ColDef<UserGroupRecord>[]>(
    () =>
      buildColumnsForPreferences<UserGroupRecord>({
        staticColumns: [
          columnHelper.accessor("name", {
            headerName: "Name",
            flex: 1,
            suppressMovable: true,
          }),
          columnHelper.accessor("members", {
            headerName: "Members",
            flex: 2,
            suppressMovable: true,
            valueFormatter: (params) =>
              memberSummary(params.value ?? [], memberLabels),
          }),
          {
            colId: "actions",
            headerName: "Actions",
            width: 200,
            // Pinned so the row's actions stay in view however narrow the
            // grid; `lockVisible` exempts the column from the visibility
            // overlay, which only knows the preference columns.
            pinned: "right",
            hide: false,
            lockVisible: true,
            resizable: false,
            suppressMovable: true,
            cellRenderer: (params: ICellRendererParams<UserGroupRecord>) => {
              const group = params.data;
              if (!group) {
                return null;
              }
              return (
                <div className={styles.rowActions}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => openEditRef.current(group)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setDeleteTarget(group);
                      setDeleteError(null);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              );
            },
          },
        ],
        dynamicColumns: [],
        preferenceOrder: DEFAULT_VISIBLE_COLUMN_IDS,
      }),
    [memberLabels],
  );

  return (
    <div>
      <div className={styles.toolbar}>
        <Typography
          variant="body2"
          color="text.secondary"
          className={styles.explanation}
        >
          {explanation.trim()}
        </Typography>
        <Button variant="contained" onClick={openCreate}>
          Add User Group
        </Button>
      </div>

      {error && (
        <Alert severity="error" className={styles.alert}>
          {error.message}
        </Alert>
      )}
      {deleteError && (
        <Alert
          severity="error"
          onClose={() => setDeleteError(null)}
          className={styles.alert}
        >
          {deleteError}
        </Alert>
      )}

      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={USER_GROUP_FILTER_DEFINITIONS}
            onFilterSelected={view.commit}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={USER_GROUP_FILTER_DEFINITIONS}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<UserGroupRecord>
          className={styles.grid}
          table={table}
          columns={columns}
          data={pageRows}
          hasNextPage={hasNextPage}
          totalRowCount={filteredGroups.length}
          loading={deriveLoadingState(userGroupsQuery)}
          noRowsContent={
            groups.length === 0
              ? "No user groups yet."
              : "No user groups match the current filters."
          }
        />
      </ResourceView.Root>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? "Edit User Group" : "Add User Group"}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <UserGroupMemberPicker
            value={pickerValue}
            onChange={setSelectedMembers}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={isPending}
          >
            {isPending ? <CircularProgress size="1rem" /> : submitLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        dialogOpen={deleteTarget !== null}
        handleClose={() => setDeleteTarget(null)}
        dialogTitle="Delete User Group"
        dialogText={`Are you sure you want to delete "${deleteTarget?.name ?? ""}"? This cannot be undone.`}
        dialogAction={async () => {
          if (!deleteTarget) {
            return;
          }
          try {
            await deleteMutation.mutateAsync(deleteTarget.user_group_id);
          } catch (err) {
            // The server rejects deletion of a group still associated
            // with a feature flag with a 409 Conflict; rephrase that
            // for the admin so they know to detach the group from any
            // referencing flag first. All other failures pass through
            // the server's message as-is.
            if (err instanceof RobotoConflictException) {
              setDeleteError(
                `Cannot delete "${deleteTarget.name}": it is referenced by a feature flag.`,
              );
            } else {
              setDeleteError(
                err instanceof Error
                  ? err.message
                  : "Failed to delete user group.",
              );
            }
          }
        }}
      />
    </div>
  );
}

/**
 * Members by name (raw id where no name resolved), or a count once the
 * list is too long to read in a cell.
 */
function memberSummary(
  members: string[],
  labels: ReadonlyMap<string, string>,
): string {
  if (!members.length) {
    return "—";
  }
  if (members.length <= MEMBER_SUMMARY_THRESHOLD) {
    return members.map((id) => labels.get(id) ?? id).join(", ");
  }
  return `${members.length} members`;
}

/** Option for a member known only by id, such as one loaded with its group. */
function memberOptionFromId(id: string): MemberOption {
  return { label: id, value: id };
}
